import { Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { ApiPaths } from '../constants/api-paths.enum';
import { NotificationService } from './notification.service';
import { Mail } from '@data/schema/mail';
import { User } from '@data/schema/user-state';
import { UserStoreService } from './user-store.service';
import { LocalStorageService } from './local-storage.service';
import { MenuItem } from '@data/schema/menuitem';
import { GAHAppResponse } from '@data/schema/gah-app-response';
import { UserGroupTerritory } from '@data/schema/user-group-territory';
import { UserRoles } from '@data/schema/user-roles';
import { CartItems } from '@data/schema/cart-items';
import { RolePriviliges } from '@data/schema/role-privileges';
import { DigitalCartService } from '@modules/assets/services/digital-cart.service';
import { NGXLogger } from 'ngx-logger';
import { LoginResponse } from '@data/schema/login-response';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { NotificationType } from '@data/schema/notificationitem';
import { SessionStorageService } from './session-storage.service';
import { OKTA_AUTH } from '@okta/okta-angular';
import { Inject } from '@angular/core';
import OktaAuth from '@okta/okta-auth-js';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  apiUrl = environment.apiUrl;
  userState: User;
  sonyEmailValidation = /^([\w\.]+)@([\w\.]+)$/;
  emailValidation =
    /(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/;

  userCreds: {
    userid: string;
    password: string;
    oktaToken:string;
  };

  preferredTerritoryId;
  preferredTerritoryName;

  mailObj: {
    to: string;
  };
  keyForMenuItems: string = null;
  requestRoleCode: string = null;
  groupRolesId: number;
  userRolePrivilegeArray: RolePriviliges[] = [];
  cartItems: { fileName: string; thumbnailUrl: string }[] = [];

  userGroupRoles: any;
  rolePrivilegesMap: any;
  userFetchDetails: any;

  private readonly _menuMap = new Subject<[]>();
  readonly menuMap$ = this._menuMap.asObservable();

  private readonly _menuItems = new BehaviorSubject<MenuItem[]>([]);
  readonly menuItems$ = this._menuItems.asObservable();

  private readonly _userGroupsTerritoryItems = new BehaviorSubject<UserGroupTerritory[]>([]);
  readonly userGroupsTerritoryItems$ = this._userGroupsTerritoryItems.asObservable();

  private readonly _userRolesItems = new BehaviorSubject<UserRoles[]>([]);
  readonly userRolesItems$ = this._userRolesItems.asObservable();

  private readonly _userRolePrivileges = new BehaviorSubject<RolePriviliges[]>([]);
  readonly userRolePrivileges$ = this._userRolePrivileges.asObservable();

  private readonly _cartItems = new BehaviorSubject<CartItems[]>([]);
  readonly cartItems$ = this._cartItems.asObservable();

  constructor(
    private http: HttpClient,
    private dialog: MatDialog,
    private router: Router,
    private userStoreService: UserStoreService,
    public notifService: NotificationService,
    private storageService: LocalStorageService,
    private location: Location,
    private log: NGXLogger,
    private digitalCartService: DigitalCartService,
    private translate: TranslateService,
    private sessionStorage: SessionStorageService,
    @Inject(OKTA_AUTH) private _oktaAuth: OktaAuth,
  ) {
    this.userState = userStoreService.userState;
  }

  /**
   * Accepts username a password to make a request for getting token
   * @modified for GAHR1553
   * @param username
   * @param password
   * @returns observable with response
   */
  login(credentials: { username: string; password: string,oktaToken:string }): Observable<any> | null {
    this.userStoreService.resetUserState();
    this.userState = this.userStoreService.userState;
    //GAHR464 - Added trim for userId
    if(credentials.oktaToken) {
      this.userState.oktaAuthenticated = true;
    }
    if ((credentials.username && credentials.password) || credentials.oktaToken) {
      this.userCreds = {
        userid: credentials.username.trim(),
        password: credentials.password,
        oktaToken:credentials.oktaToken
      };
      return this.http.post<any>(`${this.apiUrl}${ApiPaths.Token}`, this.userCreds).pipe(
        map((res: GAHAppResponse) => {
          let tokenDetails: LoginResponse = res.serviceResult;
          this.saveTokenDetails(tokenDetails.expiryEpochTime);
          this.userState.isAuthenticated = true;
          this.userState.isLegalAccepted = false;
          this.userState.username = tokenDetails.UserId; // TODO get user id from response
          this.userStoreService.userState = this.userState; // Used in header.component
          this.getUserCurrentDetails().subscribe();
          return tokenDetails;
        }),
        catchError((error) => this.handleError(error))
      );
    }
    return null;
  }

  /**
   * Logout user by removing token and other user details from local storage
   *
   * @param tokenExpired Set to true if logout is caused due to token expiry. Default is false
   */
  logout(tokenExpired: boolean = false) {
    if(this.userState?.oktaAuthenticated == true) {
      this._oktaAuth.signOut({ postLogoutRedirectUri: environment.okta.postLogoutRedirectUri });
    }
    this.sessionStorage.removeAll();
    this.userState.userPreferences.userName = this.userState?.username;
    
    this.http.post(`${this.apiUrl}${ApiPaths.Logout}`, this.userState?.userPreferences).subscribe({
      next: () => {
        this.closeIdleDialog();
        this.translate.use(this.translate.currentLang);
        this.userStoreService.userState = null;
        this.storageService.removeAll(); // Removes all localStorage entries.
        if (tokenExpired) {
          let redirectURL = this.location.path();
          this.router.navigate(['/user/login'], {
            queryParams: { 'redirectURL': redirectURL },
          });
          this.notifService.showNotification({
            message: this.translate.instant('loggedout-msg-text'),
            messageType: NotificationType.Warning,
            acrossRoutes: true,
            disableTimeout: true,
          });
        } else {
          this.router.navigate(['/user/login']);
        }
      },
      error: (err) => {
        this.closeIdleDialog();
        this.log.error('Logout was not successful!');
        this.translate.use('EN');
        this.userStoreService.userState = null;
        this.storageService.removeAll(); // Removes all localStorage entries.
        if (tokenExpired) {
          let redirectURL = this.location.path();
          this.router.navigate(['/user/login'], {
            queryParams: { 'redirectURL': redirectURL },
          });
        } else {
          this.router.navigate(['/user/login']);
        }
      },
    });
  }

  /**
   * Closes all idle popups.
   */
  closeIdleDialog() {
    this.dialog.closeAll();
  }

  /**
   * Calls refresh token endpoint to get a new token.
   */
  refreshToken(): Observable<number> {
    return this.http.post<any>(`${this.apiUrl}${ApiPaths.RefreshToken}`, {}).pipe(
      map((res: GAHAppResponse) => {
        if (res.serviceResult?.expiryEpochTime) {
          this.saveTokenDetails(res.serviceResult.expiryEpochTime);
          return res.serviceResult.expiryEpochTime;
        } else {
          return null;
        }
      })
    );
  }

  saveTokenDetails(expiryAt: number) {
    this.storageService.remove('T_EA');
    this.storageService.set('T_EA', expiryAt, false);
  }

  isTokenExpired(): boolean {
    let expiryAt: number = this.storageService.get('T_EA');
    let currentTime = Date.now();
    if (expiryAt < currentTime) return true;
    return false;
  }

  /**
   * Sends user email form forgot password page to back-end
   *
   * @param { String } email - contains to, from, subject and message
   * @returns
   */
  forgotPassword(email: Mail): Observable<any> | null {
    if (email.to) {
      this.mailObj = {
        to: email.to,
      };
      return this.http
        .post<any>(`${this.apiUrl}${ApiPaths.ForgotPasswordEndpoint}`, this.mailObj)
        .pipe(catchError((error) => this.handleError(error)));
    }
  }

  /**
   * This method can be called to update user state including menu items.
   * @returns Observable
   */
  getUserCurrentDetails() {
    this.requestRoleCode = '';
    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.UserDetails}`, this.userState.username)
      .pipe(
        map((res: GAHAppResponse) => {
          this.userFetchDetails = res.serviceResult;
          // Sorting territoryItems for group roles
          res.serviceResult['usersGroupsTerritories'] = res.serviceResult[
            'usersGroupsTerritories'
          ].sort((a, b) => (a.territory.territoryName > b.territory.territoryName ? 1 : -1));
          // Getting territory items for group roles
          this._userGroupsTerritoryItems.next(res.serviceResult['usersGroupsTerritories']);

          //Getting the email address of the user
          this.userState.emailAddress = res.serviceResult['emailAddress'];
          this.userState.gigyauid = res.serviceResult['gigyauid'];
          //Getting the full name of the user
          this.userState.fullName =
            res.serviceResult['firstName'] + ' ' + res.serviceResult['lastName'];

          //Getting the user's default group territory id
          this.userState.defaultGroupsTerritoryId = res.serviceResult.defaultGroupsTerritoryId;
          //Getting the userRoles of the user
          this._userRolesItems.next(res.serviceResult['userRoles']);

          //Setting userRoles of the user
          this.userState.userRoles = res.serviceResult['userRoles'];

          //Getting the userId of the user logging in
          this.userState.userId = res.serviceResult['userId'];

          //Getting the user account codes GAHR578
          this.userState.accountTypes = res.serviceResult['accountTypes'];

          // Setting default territory to user state
          this.userState.defaultTerritory = {
            territoryId: res.serviceResult['defaultTerritory']['territoryId'],
            territoryName: res.serviceResult['defaultTerritory']['territoryName'],
            groupTypeCode: res.serviceResult['userGroupRoles'][0]['groupTypeCode'],
          };

          this.userState.selectedTerritoryActualInd =
          res.serviceResult?.selectedTerritory?.actualTerritoryInd ??
          res.serviceResult?.defaultTerritory?.actualTerritoryInd;

          //Setting userGroupRoles
          this.userGroupRoles = res.serviceResult.userGroupRoles;

          //Setting rolePrivilegesMap
          // CHECK : can be removed from user object from backend
          this.rolePrivilegesMap = res.serviceResult.rolePrivilegesMap;

          // Setting the menuMaps available for the user
          this._menuMap.next(res.serviceResult['userMenusMap']);

          this.fetchUserPreferences(res.serviceResult.userPreferences);

          this.userState.defaultLanguageCode =
            res.serviceResult.defaultTerritory.defaultLanguageCode;
          this.userState.selectedLanguageCd = res.serviceResult.defaultLanguageCd;
          this.userStoreService.userState = this.userState;
        }),
        catchError((error) => this.handleError(error))
      );
  }

  getCartItems() {
    //Setting cart items in User state
    this.digitalCartService.getCartItems().subscribe((res) => {
      this._cartItems.next(res.serviceResult);
    });
  }

  setUserDetails() {
    for (const iterator of this.userState?.userPreferences?.userPreferenceDetails) {
      switch (iterator['parameterName']) {
        case 'territoryId': {
          this.preferredTerritoryId = iterator['parameterValues'];
          break;
        }
        case 'territoryName': {
          this.preferredTerritoryName = iterator['parameterValues'];
          break;
        }
        case 'groupTypeCode': {
          this.userState.groupTypeCode = iterator['parameterValues'];
          break;
        }
        case 'requestRoleCode': {
          this.requestRoleCode = iterator['parameterValues'];
          break;
        }
        case 'groupRoleId': {
          this.userState.groupRolesId = Number(iterator['parameterValues']);
        }
      }
    }
    if (!this.requestRoleCode) {
      this.fetchRequestRoleCode(this.userState.groupTypeCode, this.userFetchDetails['userRoles']);
    }
    // Sets the selected territory to default if value is not already set.
    this.userState.selectedTerritory = {
      territoryId: this.preferredTerritoryId,
      territoryName: this.preferredTerritoryName,
      groupTypeCode: this.userState.groupTypeCode,
    };
    this.userState.selectedTerritoryActualInd =
    this.userFetchDetails?.selectedTerritory?.actualTerritoryInd ??
    this.userFetchDetails?.defaultTerritory?.actualTerritoryInd;

    if (!this.userState.groupRolesId) {
      this.fetchGroupRolesId(this.userState.groupTypeCode, this.userFetchDetails['userRoles']);
    }
    this.storageService.set('groupRoleId', this.userState.groupRolesId);
    // Setting the default date format of territory on initial log in
      this.userState.defaultDateFormat =
        this.userFetchDetails['selectedTerritory']['defaultDateFormatCode'] == 'DF1'
          ? 'MM/DD/YYYY'
          : 'DD/MM/YYYY';

    // Setting the groupTerritoryId for the user logged in
    if (this.userState.groupTypeCode) {
      for (let x of this.userFetchDetails['usersGroupsTerritories']) {
        if (
          x.groupTypeCode == this.userState.groupTypeCode &&
          Number(this.userState.selectedTerritory?.territoryId) === x['territory']['territoryId']
        ) {
          this.userState.groupsTerritoryId = x.groupsTerritoryId;
          break;
        }
      }
    }
    //Generating the key for fetching the menuItems based on groupRole and requestRoleCode
    this.keyForMenuItems = this.userState.groupTypeCode + this.requestRoleCode;
    // Getting menu items
    this._menuItems.next(this.userFetchDetails?.userMenusMap[this.keyForMenuItems]);

    //Getting that whether logged-in user is Admin
    this.userState.isAdmin = this.userFetchDetails['admin'];

    this.userState.userPrivileges = this.userFetchDetails['rolePrivilegesMap'];
    
    this.fetchRolePrivileges().subscribe();
    this.fetchUpdatedUserMenuMap().subscribe();
    this.userStoreService.userState = this.userState;
  }

  updateCartItems(cartItemsUpdated: any) {
    this._cartItems.next(cartItemsUpdated);
  }

  get menuItems(): MenuItem[] {
    return this._menuItems.getValue();
  }

  set menuItems(menuItemsUpdated: MenuItem[]) {
    this._menuItems.next(menuItemsUpdated);
  }

  get usersGroupsTerritoryItems(): UserGroupTerritory[] {
    return this._userGroupsTerritoryItems.getValue();
  }

  /**
   * @function Sets the requestRoleCode for the first log in
   * @param groupTypeCd
   * @param userRolesArray
   */
  fetchRequestRoleCode(groupTypeCd: any, userRolesArray: any) {
    for (let x of userRolesArray) {
      //first time login
      if (x['roles']['groupTypeCode'] == groupTypeCd && x['roles']['activeInd'] == 1) {
        this.requestRoleCode = x['roles']['requestRoleCode'];
        break;
      }
    }
    this.userState.userPreferences.userPreferenceDetails.push({
      parameterName: 'requestRoleCode',
      parameterValues: this.requestRoleCode,
    });
  }

  fetchGroupRolesId(groupTypeCd: any, userRolesArray: any) {
    for (let x of userRolesArray) {
      this.userState.userRoles = x;
      if (x['roles']['groupTypeCode'] == groupTypeCd) {
        this.groupRolesId = x['roles']['groupRolesId'];
        break;
      }
    }
    this.userState.userPreferences.userPreferenceDetails.push({
      parameterName: 'groupRoleId',
      parameterValues: this.groupRolesId.toString(),
    });
    this.userState.groupRolesId = this.groupRolesId;
  }

  fetchRolePrivileges(): Observable<any> | null {
    let params = new HttpParams()
      .set('groupRoleId', this.userState.groupRolesId)
      .set('territoryId', this.userState.selectedTerritory?.territoryId);
    return this.http.get<any>(`${this.apiUrl}/${ApiPaths.fetchRolePrivileges}`, { params }).pipe(
      map((res) => {
        this.userRolePrivilegeArray = res.serviceResult;
        this._userRolePrivileges.next(this.userRolePrivilegeArray);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  fetchUpdatedUserMenuMap(): Observable<any> | null {
    let payload = {
      rolePrivilegesMap: this.rolePrivilegesMap,
      userGroupRoles: this.userGroupRoles,
      userPreferences: this.userState.userPreferences,
    };

    return this.http.post<any>(`${this.apiUrl}${ApiPaths.fetchUserMenuMap}`, payload).pipe(
      map((res) => {
        this._menuMap.next(res.serviceResult);
      }),
      catchError((error) => this.handleError(error))
    );
  }

  fetchUserPreferences(userPreferencesSet: any) {
    let params = new HttpParams().set('userId', this.userState.userId);
    this.http
      .get<any>(`${environment.apiUrl}${ApiPaths.FetchUpdatedUserPreferences}`, { params })
      .subscribe({
        next: (res) => {
          if (res.serviceResult?.userName !== null) {
            this.userState.userPreferences = res.serviceResult;
          } else {
            this.userState.userPreferences = userPreferencesSet;
          }
          this.setUserDetails();
        },
        error: () => {
          this.logout();
        },
      });
  }

  updateUserPreferences(): Observable<any> | null {
    this.userState.userPreferences.userName = this.userState.username;
    return this.http
      .post<any>(`${this.apiUrl}${ApiPaths.UpdateUserPreferences}`, this.userState.userPreferences)
      .pipe(catchError((error) => this.handleError(error)));
  }

  /**
   * @param error HttpErrorResponse object
   */
  private handleError(error: HttpErrorResponse) {
    let errorMsg: {
      message: string;
      errorCode: number;
    } = {
      message:
        error.error && error.error.errorDescription
          ? error.error.errorDescription
          : this.translate.instant('unknown-error-msg-text'),
      errorCode: error.status,
    };
    return throwError(errorMsg);
  }

  /**
   * Function - Get request role for a grouprole territory and userid
   * @param groupRoleId 
   * @param territoryId 
   * @returns requestRole
   */
  getRequestRole(groupTypeCode: string, territoryId:number): Observable<any> | null {
    let params = new HttpParams()
    .set('groupTypeCode',groupTypeCode)
    .set('territoryId',territoryId)
    .set('userId', this.userStoreService.userState.userId)

    return this.http.get(`${environment.apiUrl}${ApiPaths.FetchRequestRolecode}`, { params }).pipe(
      map((res: GAHAppResponse) => {
        return res.serviceResult;
      }),
      catchError((error) => this.handleError(error))
    );
  }
}